import { monitorStatusTextCssClass } from '@fe/core';
import { SvgIcon } from '@progress/kendo-react-common';
import { GridCustomRowProps } from '@progress/kendo-react-grid';
import clsx from 'clsx';
import { FlightStatus } from '../../../../../common/enums';
import { FleetMonitorFlightDto } from '../../../../../flights/interop/flightDto';
import { DateTimeFormatter, UnitFormatter } from '../../../../../formatters';
import { AirplaneIcon } from '../../../../../icons/icons';
import FlightStatusBadge from '../../../../components/FlightStatusBadge';
import MonitorStatusStripe from '../../../../components/monitor-status/MonitorStatusStripe';

export function FleetMonitorItem(extra: {
    show5thRow: boolean;
    showFlightBgColor: boolean;
    collapsedView: boolean;
    onClick: (id: number) => void;
    onContextMenu: (event: MouseEvent, id: number) => void;
}) {
    return function (props: GridCustomRowProps) {
        const flight = props.dataItem as FleetMonitorFlightDto;
        const progressRight = getProgress(flight);

        return (
            <tr {...props.trProps}>
                <td
                    className="fleet-monitor-item d-flex gap-1 p-1"
                    onClick={() => extra.onClick(flight.id)}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        extra.onContextMenu(e.nativeEvent, flight.id);
                    }}>
                    <MonitorStatusStripe className="flex-shrink-0" monitorStatus={flight.monitorStatus} />

                    <div className="fm-container w-100">
                        {/* ACID */}
                        <div className="fm-primary fw-bold">{flight.acid || '- - - -'}</div>

                        {/* ORG - DEST */}
                        <div className="d-flex gap-1 align-items-center">
                            <div className={clsx('fm-primary fw-bold', monitorStatusTextCssClass[flight.originMonitorStatus])}>
                                {flight.origin || 'N/A'}
                            </div>
                            {flight.edct && <div className="fm-border-icon">CT</div>}

                            <div className="flex-grow-1"></div>
                            {flight.holding && <div className="fm-border-icon">H</div>}

                            <div className="flex-grow-1"></div>

                            {flight.lastDestination && (
                                <>
                                    <div className="fm-border-icon">D</div>
                                    <div className="fm-primary fw-bold text-decoration-line-through">{flight.lastDestination}</div>
                                </>
                            )}

                            <div className={clsx('fm-primary fw-bold', monitorStatusTextCssClass[flight.destinationMonitorStatus])}>
                                {flight.destination || 'N/A'}
                            </div>
                        </div>

                        {/* FlightStatus */}
                        <div>
                            <FlightStatusBadge
                                className="fm-secondary"
                                useColorBackground={extra.showFlightBgColor}
                                flightStatus={flight.status}
                                ground={flight.onGround}
                                style={{ fontSize: '0.9em' }}
                            />
                        </div>

                        {/* Dep Arr Times */}
                        <div className="fm-secondary-tag secondary-text-color d-flex justify-content-between">
                            <div className={clsx(flight.edct && 'fm-dashed-border')}>
                                <span>{DateTimeFormatter.formatTime(flight.departureTime) || '--:--'}</span>
                                {!!flight.departureTimeDeviation && (
                                    <span className={flight.departureTimeDeviation > 0 ? 'fm-text-red' : 'fm-text-green'}>
                                        {flight.departureTimeDeviation > 0 ? '+' : ''}
                                        {flight.departureTimeDeviation}
                                    </span>
                                )}
                            </div>
                            <div>
                                <span>{DateTimeFormatter.formatTime(flight.arrivalTime) || '--:--'}</span>
                                {!!flight.arrivalTimeDeviation && (
                                    <span className={flight.arrivalTimeDeviation > 0 ? 'fm-text-red' : 'fm-text-green'}>
                                        {flight.arrivalTimeDeviation > 0 ? '+' : ''}
                                        {flight.arrivalTimeDeviation}
                                    </span>
                                )}
                            </div>
                        </div>

                        {!extra.collapsedView && (
                            <>
                                {/* ACTYPE */}
                                <div className="fm-secondary secondary-text-color">{flight.acType || '- - - -'}</div>

                                {/* ProgressLine */}
                                <div className="fm-progress-container w-100 position-relative d-flex align-items-center">
                                    <div className="fm-line w-100"></div>
                                    <div className="fm-vert fm-out"></div>
                                    <div className="fm-vert fm-off"></div>
                                    <div className="fm-vert fm-on"></div>
                                    <div className="fm-vert fm-in"></div>
                                    {flight.status >= FlightStatus.Departed && flight.status <= FlightStatus.Hold && (
                                        <>
                                            <div className="fm-progress-line" style={{ right: progressRight }}></div>
                                            <SvgIcon
                                                className={clsx('fm-progress-icon position-absolute', flight.holding && 'fm-dashed-border')}
                                                icon={AirplaneIcon}
                                                style={{ right: progressRight }}
                                            />
                                        </>
                                    )}
                                </div>

                                {/* RegID */}
                                <div className="fm-secondary secondary-text-color">{flight.regId || '- - - -'}</div>

                                {/* OOOI arrows */}
                                <div className="align-items-centers fm-secondary-tag secondary-text-color position-relative">
                                    <svg
                                        width="8"
                                        height="8"
                                        className={clsx('fm-out-arrow fm-arrow', flight.out && 'active')}
                                        viewBox="0 0 10 10">
                                        <path d="M0,0 L0,10 L10,5 Z" />
                                    </svg>
                                    <svg
                                        width="8"
                                        height="8"
                                        className={clsx('fm-off-arrow fm-arrow', flight.off && 'active')}
                                        viewBox="0 0 10 10">
                                        <path d="M0,10 L10,10 L5,0 Z" fill="@GetArrowColor(Data.Off)" />
                                    </svg>
                                    <svg
                                        width="8"
                                        height="8"
                                        className={clsx('fm-on-arrow fm-arrow', flight.on && 'active')}
                                        viewBox="0 0 10 10">
                                        <path d="M0,0 L10,0 L5,10 Z" fill="@GetArrowColor(Data.On)" />
                                    </svg>
                                    <svg
                                        width="8"
                                        height="8"
                                        className={clsx('fm-in-arrow fm-arrow', flight.in && 'active')}
                                        viewBox="0 0 10 10">
                                        <path d="M10,0 L10,10 L0,5 Z" fill="@GetArrowColor(Data.In)" />
                                    </svg>

                                    <div className="fm-oooi-text d-flex justify-content-between align-items-center position-relative">
                                        <span className="fm-out-off secondary-text-color fm-secondary">{flight.taxiOutTime}</span>
                                        <div>
                                            {UnitFormatter.formatSpeed(flight.speed)}{' '}
                                            {UnitFormatter.formatAltitude(flight.altitude) || '--ft'}
                                        </div>
                                        <span className="fm-on-in secondary-text-color fm-secondary">{flight.taxiInTime}</span>
                                    </div>
                                </div>

                                {extra.show5thRow && (
                                    <>
                                        <div></div>
                                        {/* Alt and fuels */}
                                        <div className="d-flex justify-content-between">
                                            <div className="fm-secondary-tag secondary-text-color">
                                                <span>Alt: </span>
                                                <span className={monitorStatusTextCssClass[flight.destinationAlternate1MonitorStatus]}>
                                                    {flight.destinationAlternate1 || '----'}
                                                </span>
                                                <span> </span>
                                                <span className={monitorStatusTextCssClass[flight.destinationAlternate2MonitorStatus]}>
                                                    {flight.destinationAlternate2 || '----'}
                                                </span>
                                            </div>
                                            {flight.fuelOnboard && (
                                                <div>
                                                    <div className="fm-border-icon fm-fuel-icon d-inline-block">A</div>
                                                    <span className="ms-1 fm-secondary">
                                                        {UnitFormatter.formatFuel(flight.fuelOnboard)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </td>
            </tr>
        );
    };
}

function getProgress(flight: FleetMonitorFlightDto): string {
    // If we're already 'in' or 'on' progress doesn't matter just put the flight at the 'in' or 'on' triangle
    if (flight.in) {
        return '7px';
    } else if (flight.on) {
        return '17px';
    } else if (flight.progressPercentage !== null) {
        // If we have progress use it
        return `calc(${(1 - flight.progressPercentage!).toFixed(2)} * (100% - 34px) + 17px)`;
    } else {
        if (flight.off) {
            return 'calc(100% - 17px)';
        }

        // We don't have anything about this flight yet, no progress or OOOI time. Put it at 'out'.
        return 'calc(100% - 7px)';
    }
}
